import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { broadcastApi } from './broadcastApi';
import type { Broadcast, BroadcastCreateRequest, BroadcastUpdateRequest } from '../model/types';

export const broadcastQueryKeys = {
  all: ['broadcasts'] as const,
  list: (assistantId: string) => [...broadcastQueryKeys.all, 'list', assistantId] as const,
  detail: (assistantId: string, broadcastId: string) =>
    [...broadcastQueryKeys.all, 'detail', assistantId, broadcastId] as const,
  recipients: (assistantId: string, broadcastId: string) =>
    [...broadcastQueryKeys.all, 'recipients', assistantId, broadcastId] as const,
  stats: (assistantId: string, broadcastId: string) =>
    [...broadcastQueryKeys.all, 'stats', assistantId, broadcastId] as const,
};

const retry = (failureCount: number, error: unknown) => {
  const status = (error as { response?: { status?: number } }).response?.status;
  if (status === 401 || status === 403) return false;
  return failureCount < 1;
};

/** Идёт ли рассылка прямо сейчас (для автополлинга прогресса). */
const isActive = (s: Broadcast['status']) => s === 'queued' || s === 'sending';

export function useBroadcasts(assistantId: string) {
  return useQuery({
    queryKey: broadcastQueryKeys.list(assistantId),
    queryFn: () => broadcastApi.list(assistantId),
    staleTime: 10_000,
    retry,
    // Пока есть активная рассылка — обновляем список каждые 3с (живой прогресс).
    // Аргумент читаем устойчиво к версии TanStack (v5 даёт query, v4 — data).
    refetchInterval: (arg: any) => {
      const data = (arg?.state?.data ?? arg) as Broadcast[] | undefined;
      return Array.isArray(data) && data.some((b) => isActive(b.status)) ? 3000 : false;
    },
  });
}

export function useBroadcast(assistantId: string, broadcastId: string | null) {
  return useQuery({
    queryKey: broadcastQueryKeys.detail(assistantId, broadcastId ?? ''),
    queryFn: () => broadcastApi.get(assistantId, broadcastId as string),
    enabled: Boolean(broadcastId),
    staleTime: 10_000,
    retry,
    refetchInterval: (arg: any) => {
      const b = (arg?.state?.data ?? arg) as Broadcast | undefined;
      return b && isActive(b.status) ? 3000 : false;
    },
  });
}

export function useRecipientsPreview(assistantId: string, broadcastId: string | null) {
  return useQuery({
    queryKey: broadcastQueryKeys.recipients(assistantId, broadcastId ?? ''),
    queryFn: () => broadcastApi.previewRecipients(assistantId, broadcastId as string),
    enabled: Boolean(broadcastId),
    staleTime: 10_000,
    retry,
  });
}

/**
 * Статистика доставки. Пока рассылка активна — обновляем каждые 3с вместе с
 * прогрессом; после завершения данные статичны, поллинг выключается.
 */
export function useBroadcastStats(
  assistantId: string,
  broadcastId: string | null,
  isRunning: boolean,
) {
  return useQuery({
    queryKey: broadcastQueryKeys.stats(assistantId, broadcastId ?? ''),
    queryFn: () => broadcastApi.getStats(assistantId, broadcastId as string),
    enabled: Boolean(broadcastId),
    staleTime: 5_000,
    retry,
    refetchInterval: isRunning ? 3000 : false,
  });
}

function useCacheBroadcast(assistantId: string) {
  const qc = useQueryClient();
  return (b: Broadcast) => {
    qc.setQueryData(broadcastQueryKeys.detail(assistantId, b.id), b);
    qc.invalidateQueries({ queryKey: broadcastQueryKeys.list(assistantId) });
  };
}

export function useCreateBroadcast(assistantId: string) {
  const cache = useCacheBroadcast(assistantId);
  return useMutation({
    mutationFn: (payload: BroadcastCreateRequest) => broadcastApi.create(assistantId, payload),
    onSuccess: cache,
  });
}

export function useUpdateBroadcast(assistantId: string) {
  const cache = useCacheBroadcast(assistantId);
  return useMutation({
    mutationFn: ({
      broadcastId,
      payload,
    }: {
      broadcastId: string;
      payload: BroadcastUpdateRequest;
    }) => broadcastApi.update(assistantId, broadcastId, payload),
    onSuccess: cache,
  });
}

export function useDeleteBroadcast(assistantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (broadcastId: string) => broadcastApi.remove(assistantId, broadcastId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: broadcastQueryKeys.list(assistantId) });
    },
  });
}

export function useUploadBroadcastMedia(assistantId: string) {
  const cache = useCacheBroadcast(assistantId);
  return useMutation({
    mutationFn: ({ broadcastId, file }: { broadcastId: string; file: File }) =>
      broadcastApi.uploadMedia(assistantId, broadcastId, file),
    onSuccess: cache,
  });
}

export function useDeleteBroadcastMedia(assistantId: string) {
  const cache = useCacheBroadcast(assistantId);
  return useMutation({
    mutationFn: ({ broadcastId, index }: { broadcastId: string; index: number }) =>
      broadcastApi.deleteMedia(assistantId, broadcastId, index),
    onSuccess: cache,
  });
}

export function useSendBroadcast(assistantId: string) {
  const cache = useCacheBroadcast(assistantId);
  return useMutation({
    mutationFn: (broadcastId: string) => broadcastApi.send(assistantId, broadcastId),
    onSuccess: cache,
  });
}

export function useCancelBroadcast(assistantId: string) {
  const cache = useCacheBroadcast(assistantId);
  return useMutation({
    mutationFn: (broadcastId: string) => broadcastApi.cancel(assistantId, broadcastId),
    onSuccess: cache,
  });
}
